import { Injectable, inject } from '@angular/core';
import { HowdenHttpClientService, IResponse } from '@howdeniberia/core-front';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_PATH_APPS } from 'src/app/core/constants';
import { IMasterValue } from 'src/app/core/models/api/master';

interface IRawMaster { id?: unknown; value?: unknown; description?: string; text?: string }

const GENERAL_PATHS = {
  sports: 'api/general/sports',
  levelLife: 'api/general/level-life',
  children: 'api/general/children',
  generalRegime: 'api/general/general-regime',
  reta: 'api/general/special-regime-selfemployed-workers',
  mutual: 'api/general/mutual',
  motorcycles: 'api/general/motorcycles'
} as const;

@Injectable({ providedIn: 'root' })
export class MasterDataService {
  private http = inject(HowdenHttpClientService);

  private readonly base = API_PATH_APPS.API_PERSONAL_INSURANCE;

  private load(path: string): Observable<IMasterValue[]> {
    return this.http.get<IResponse<IRawMaster[]>>(this.base, path).pipe(
      map(r => this.normalize(r?.result ?? [])),
      catchError(() => of([]))
    );
  }

  private normalize(items: IRawMaster[]): IMasterValue[] {
    if (!Array.isArray(items) || items.length === 0) { return []; }
    const seen = new Set<string>();
    const mapped: IMasterValue[] = [];

    for (const it of items) {
      const id = (it?.id ?? it?.value);
      const desc = (it?.description ?? it?.text ?? '').toString().trim();

      if (id === null || id === undefined) { continue; }
      const key = String(id);

      if (!seen.has(key)) {
        seen.add(key);
        mapped.push({ id: key, description: desc });
      }
    }

    return mapped.sort((a, b) => (a.description ?? '').localeCompare(b.description ?? ''));
  }


  getPaymentTypes(): Observable<IMasterValue[]> {
    return of([
      { id: '1', description: 'Anual' },
      { id: '2', description: 'Semestral' },
      { id: '4', description: 'Trimestral' },
      { id: '12', description: 'Mensual' }
    ]);
  }

  getActivities(): Observable<IMasterValue[]> {
    return of([
      { id: 'cp', description: 'Cuenta propia' },
      { id: 'ca', description: 'Cuenta ajena' }
    ]);
  }

  getSports(): Observable<IMasterValue[]> {
    return this.load(GENERAL_PATHS.sports);
  }

  getLivingStandards(): Observable<IMasterValue[]> {
    return this.load(GENERAL_PATHS.levelLife);
  }

  getChildren(): Observable<IMasterValue[]> {
    return this.load(GENERAL_PATHS.children);
  }

  getDeadlinesIPT(): Observable<IMasterValue[]> {
    const items = Array.from({ length: 50 }, (_, i) => i + 1)
      .map(n => ({ id: n.toString(), description: n.toString() }));
    return of(items);
  }

  getContributionsGeneralReg(): Observable<IMasterValue[]> {
    return this.load(GENERAL_PATHS.generalRegime);
  }

  getContributionsRETA(): Observable<IMasterValue[]> {
    return this.load(GENERAL_PATHS.reta);
  }

  getMutualIncomes(): Observable<IMasterValue[]> {
    return this.load(GENERAL_PATHS.mutual);
  }

  getMotorbikes(): Observable<IMasterValue[]> {
    return this.load(GENERAL_PATHS.motorcycles);
  }

  getMaritalStatuses(): Observable<IMasterValue[]> {
    return of([
      { id: '0', description: 'Soltero/a' },
      { id: '1', description: 'Casado/a' }
    ]);
  }
}
